/**
 * Admin Authentication Service
 * Handles JWT token exchange and storage for admin API access
 */

const JWT_STORAGE_KEY = 'admin_jwt_token';

/**
 * Get the admin API base URL
 * In development: uses proxy (/api/admin)
 * In production: uses full Cloud Function URL
 */
function getAdminApiUrl(): string {
    const apiUrl = import.meta.env.VITE_ADMIN_API_URL;
    if (apiUrl) {
        return apiUrl;
    }
    // Fallback to proxy for development
    return '/api/admin';
}

/**
 * Exchange Firebase credentials for admin JWT token
 * Call this after Firebase authentication with email/password
 */
export async function exchangeFirebaseTokenForJWT(email: string, password: string): Promise<string> {
    // Call admin API login endpoint with email/password
    // The backend will verify with Firebase Auth and return JWT
    const apiUrl = getAdminApiUrl();
    const response = await fetch(`${apiUrl}/admin/auth/login`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to authenticate with admin API');
    }

    const { token } = await response.json();

    // Store JWT in sessionStorage
    sessionStorage.setItem(JWT_STORAGE_KEY, token);

    return token;
}

/**
 * Get stored JWT token
 * Returns null if not authenticated
 */
export function getStoredJWT(): string | null {
    return sessionStorage.getItem(JWT_STORAGE_KEY);
}

/**
 * Clear stored JWT token
 */
export function clearStoredJWT(): void {
    sessionStorage.removeItem(JWT_STORAGE_KEY);
}

/**
 * Refresh JWT token using Firebase ID token
 * Call this when JWT is missing but Firebase auth exists
 */
export async function refreshJWTFromFirebase(): Promise<string> {
    const { auth } = await import('./firebase');
    const user = auth.currentUser;

    if (!user) {
        throw new Error('Not authenticated. Please login again.');
    }

    // Get Firebase ID token
    const firebaseToken = await user.getIdToken();

    // Exchange Firebase ID token for admin JWT
    const apiUrl = getAdminApiUrl();
    const response = await fetch(`${apiUrl}/admin/auth/refresh`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${firebaseToken}`,
        },
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to refresh admin token');
    }

    const { token } = await response.json();

    // Store new JWT in sessionStorage
    sessionStorage.setItem(JWT_STORAGE_KEY, token);

    return token;
}

/**
 * Get admin JWT token for API calls
 * Returns stored token or attempts to refresh from Firebase
 */
export async function getAdminJWT(): Promise<string> {
    let token = getStoredJWT();

    // If no token, try to refresh from Firebase auth
    if (!token) {
        try {
            token = await refreshJWTFromFirebase();
        } catch (error) {
            throw new Error('Not authenticated. Please login again.');
        }
    }

    return token;
}
